import apiClient from './ApiClient';
import { useAppContext } from './AppContext';
import { useState } from 'react';
import { useEffectAsync } from '../helpers';

type AclTree = Record<
	string,
	Record<
		string,
		{
			allowed: boolean;
		}
	>
>;

type AclResource = {
	id: string;
	key: string;
};

type AclPermission = {
	resourceId: string;
	key: string;
};

export const getAclTree = async () => {
	const tree = await apiClient.get('/acl/tree/me');

	return tree?.data || {};
};

export const buildAcl = async (): Promise<AclTree> => {
	const {
		resources = [],
		permissions = [],
	}: {
		resources: AclResource[];
		permissions: AclPermission[];
	} = await getAclTree();

	const tempAcl: AclTree = {};

	for (const permission of permissions) {
		const resource = resources.find(r => r.id === permission.resourceId);

		if (!resource?.key) {
			continue;
		}

		if (!tempAcl[resource.key]) {
			tempAcl[resource.key] = {};
		}

		tempAcl[resource.key][permission.key] = {
			allowed: true,
		};
	}

	return tempAcl;
};

const verifyPermission = (
	acl: AclTree | null,
	resource: string,
	permission?: string,
) => {
	if (!acl) {
		return false;
	}

	const r = acl[resource];

	if (!r) {
		return false;
	}

	const p = r[permission || 'view'];

	if (!p) {
		return false;
	}

	return p.allowed;
};

export const isAllowed = (resource: string, permission?: string) => {
	const { aclData } = useAppContext();

	const [acl, setAcl] = useState<AclTree | null>(null);

	useEffectAsync(async () => {
		if (!aclData.loading) {
			aclData.loading = true;

			aclData.data = await buildAcl();

			aclData.fireListeners();
		}

		if (Object.keys(aclData.data || {}).length) {
			setAcl(aclData.data);
		} else {
			aclData.listeners.push(() => {
				setAcl(aclData.data);
			});
		}
	}, []);

	return verifyPermission(acl, resource, permission);
};

export const useStandaloneAcl = () => {
	return {
		isAllowed: (acl: AclTree, resource: string, permission?: string) => {
			return verifyPermission(acl, resource, permission);
		},

		loadAcl: async () => {
			return await buildAcl();
		},
	};
};
